<script setup lang="ts">
import { max_value as maxValue, min_value as minValue, numeric } from '@vee-validate/rules';
import { Field } from 'vee-validate';
import { computed, ref, watch } from 'vue';

import Checkbox from '@/components/base/form/Checkbox.vue';
import InlineError from '@/components/base/form/InlineError.vue';
import Input from '@/components/base/form/Input.vue';
import ADLineItemDetails from '@/components/checkout/ADLineItemDetails.vue';
import { EditableLineItem } from '@/components/checkout/steps/multiship/MultiShip.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { FlexibleMessageValidator, useForm } from '@/composables/useForm';
import money from '@/filters/money';
import { findMessage, isGreetingCardLineItem } from '@/utils/cart';
import { getUpdatedUnitName } from '@/utils/product';

const props = defineProps<{
  index: number;
  item: EditableLineItem;
}>();

const quantitySelected = defineModel<number>({ default: 0 });

const { errorMessages, validatorFailed } = useForm();

const isChecked = ref(quantitySelected.value > 0);

const validator: FlexibleMessageValidator<string> = (value) => {
  const { quantityAvailable } = props.item;
  if (value === '') return errorMessages.quantity.numeric;
  if (!minValue(value, [0])) return errorMessages.quantity.min_value.replace('{min}', '0');
  if (!numeric(value)) return errorMessages.quantity.numeric;
  if (!maxValue(value, [quantityAvailable])) {
    return errorMessages.quantity.max_value.replace('{max}', String(quantityAvailable));
  }
  return true;
};

const { flags } = useFeatureFlags();
const formattedUnitName = computed(() =>
  flags.testVariantNameDisplay && props.item.variant.variantName
    ? getUpdatedUnitName(props.item.variant.variantName, flags.testVariantNameDisplay)
    : props.item.variant.variantName?.en,
);

watch(isChecked, () => {
  quantitySelected.value = isChecked.value ? props.item.quantityAvailable : 0;
});
</script>

<template>
  <div class="flex-row items-center mt-3 md:mt-6">
    <Checkbox
      v-model="isChecked"
      class="content-start self-start flex-1 font-normal align-top"
      data-test="select-line-item-checkbox"
    >
      <div data-test="select-line-item-label" class="ml-1">
        <div
          class="text-sm font-semibold md:text-base line-clamp-2"
          data-test="product-title-line-item"
        >
          {{ item.name.en }}
        </div>
        <div class="flex">
          <div v-if="isGreetingCardLineItem(item)" class="text-xs">
            Message: {{ findMessage(item) }}
          </div>
          <div v-else-if="item.variant.variantName" class="text-xs">
            {{ formattedUnitName }}
          </div>
          <div
            v-if="item.custom?.fields.markedAsGift && !item.customizationsDescription"
            class="text-xs"
            :class="item.variant.variantName && 'ml-2 pl-2 border-l border-solid border-gray-300'"
          >
            This is a gift
          </div>
        </div>
        <ADLineItemDetails
          v-if="item.custom?.fields.autoDeliveryInterval"
          :autoDeliveryIntervalWeeks="item.custom.fields.autoDeliveryInterval / 7"
        />
        <div v-if="item.customizationsDescription" class="text-xs">
          <span>
            {{ item.customizationsDescription }}
          </span>
          <span
            v-if="item.custom?.fields.markedAsGift"
            class="pl-2 ml-2 border-l border-gray-300 border-solid"
          >
            This is a gift
          </span>
        </div>
        <div v-if="item.custom && item.custom.fields.giftCertificateValue" class="text-xs">
          {{ money(item.custom.fields.giftCertificateValue) }}
        </div>
      </div>
    </Checkbox>

    <Field
      v-model.number="quantitySelected"
      :name="`lineItems[${index}].quantity`"
      :rules="validator"
      v-slot="{ errors, field, meta }"
    >
      <div class="w-16 ml-6">
        <Input
          v-bind="field"
          type="number"
          min="0"
          :max="item.quantityAvailable"
          :id="`line-item-quantity:${item.id}`"
          :invalid="validatorFailed(meta)"
          aria-label="quantity"
          :data-test="`line-item-quantity:${item.id}`"
        />
        <InlineError
          :errors="errors"
          :data-test="`line-item-quantity:${item.id}-validation-error`"
          fieldName="Multiship Item Quantity"
          :invalid="validatorFailed(meta)"
        />
      </div>
    </Field>
    <div class="w-10 text-sm text-center md:text-base">
      {{ item.quantityAvailable - quantitySelected }}
    </div>
  </div>
</template>
